import { useQuery, useQueryClient } from '@tanstack/react-query';
import { stockExitsNewAPI } from '../../services/api';
import { StockExitNew, StockExitNewListResponse } from '../../types/stockExitNew';

/**
 * Query hooks for Stock Exits (server-paginated list + deleted list).
 *
 * Key root MUST be 'stock-exits' — the slug the realtime-invalidation
 * middleware broadcasts for /api/stock-exits. Invalidating the root refreshes
 * every paginated/filtered variant and the deleted list at once.
 */
export interface StockExitsListParams {
  skip: number;
  limit: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  articleId?: number;
  serviceId?: number;
  extra?: Record<string, any>;
}

export const stockExitsKeys = {
  root: ['stock-exits'] as const,
  list: (params: StockExitsListParams) => ['stock-exits', 'list', params] as const,
  deleted: ['stock-exits', 'deleted'] as const,
};

export function useStockExitsList(params: StockExitsListParams, enabled: boolean) {
  return useQuery<StockExitNewListResponse>({
    queryKey: stockExitsKeys.list(params),
    queryFn: () =>
      stockExitsNewAPI.getPaginated(
        params.skip,
        params.limit,
        params.search,
        params.startDate,
        params.endDate,
        params.articleId,
        params.serviceId,
        undefined,
        params.extra,
      ),
    enabled,
    keepPreviousData: true,
  });
}

export function useDeletedStockExits(enabled: boolean) {
  return useQuery<StockExitNew[]>({
    queryKey: stockExitsKeys.deleted,
    queryFn: () => stockExitsNewAPI.getDeleted(),
    enabled,
  });
}

export function useInvalidateStockExits() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: stockExitsKeys.root });
}
